import { useRef, useEffect, useState, useMemo } from "react"
import { Box, Center, Text, Button } from "@chakra-ui/react"
import { MemoryRouter, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { SectionKey, sections, ChunkStaleError } from "../../sections"
import { refreshApp } from "../../hooks/useVersionCheck"
import { graphqlUri, graphqlUriLegacy, restEndpoint } from "../../config"
import { useAuth } from "../../auth/AuthProvider"
import { SectionFeatures } from "@hydrologiq/shared/types/sectionFeatures"
import { TenantProfile } from "@hydrologiq/gql-experience/graphql"
import { shadowGlobalCss } from "./shadowStyles"
import {
  extractSubRoute,
  extractSectionSubRoute,
  createRouteChangeHandler,
  isGlobalSettingsRoute,
  parseRouteFromPath,
} from "../../utils/routeSync"
import { SectionErrorBoundary } from "./SectionErrorBoundary"
import { useSectionLoadingState } from "./SectionLoadingStates"
import { useSection } from "../../hooks/useSection"
import { RootState } from "../../state/store"
import { resolveSection } from "../../utils/promotionResolver"
import { loadPrototypeBundle } from "../../utils/prototypeBundleLoader"
import { buildSectionContext } from "../../utils/buildSectionContext"
import { useColorMode } from "@hydrologiq/shared/chakra/ColorMode"

// Debug logger — prefixes all messages with [SectionLoader:<name>]
const debug = (sectionName: string, ...args: unknown[]) => {
  console.debug(`[SectionLoader:${sectionName}]`, ...args)
}

interface SectionLoaderProps {
  sectionName: SectionKey
  sidebarWidth?: { base: number; md: number }
  sectionFeatures?: SectionFeatures
  onLoadStateChange?: (sectionName: string, isLoading: boolean) => void
  isActive?: boolean
  mountedSections?: string[]
  tenantCopy?: any
  tenantFeatures?: any
  profile?: TenantProfile
  user?: any // User from hub's auth context
  previewBundleUrl?: string | null
}

export const SectionLoader = ({
  sectionName,
  sidebarWidth = { base: 55, md: 75 },
  sectionFeatures,
  onLoadStateChange,
  isActive = true,
  mountedSections = [],
  tenantCopy,
  tenantFeatures,
  profile,
  user,
  previewBundleUrl,
}: SectionLoaderProps) => {
  const { updateSectionSubRoute } = useSection()

  // Get reload trigger for this section to force re-initialization when needed
  const { sectionReloadTriggers } = useSelector((state: RootState) => state.section)
  const reloadTrigger = sectionReloadTriggers[sectionName] || 0

  // Read promotion overrides from tenant preferences
  const promotionOverrides = useSelector((state: RootState) => state.preferences.promotionOverrides)

  // Track previous isActive state to detect activation transitions
  const prevIsActiveRef = useRef<boolean>(isActive)

  const containerRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<ShadowRoot | null>(null)
  const sectionLoadedRef = useRef<string | null>(null)
  const bodyRef = useRef<HTMLElement | null>(null)
  const locationRef = useRef<string>("")
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingNavigationRef = useRef<string | null>(null)
  const rootCreatedRef = useRef<boolean>(false)
  const lastSubRouteRef = useRef<string>("/")
  const routeChangeHandlerRef = useRef<((fullPath: string) => void) | null>(null)
  const browserNavigationCallbackRef = useRef<((subRoute: string) => void) | null>(null)

  // The hub owns the Colour Mode. Sections cannot see the `dark` class on <html>
  // across the shadow boundary, so each shadow `#section-body` carries its own
  // `light` / `dark` class, which Chakra's colour conditions key off.
  const { colorMode } = useColorMode()
  const sectionColourMode = colorMode === "dark" ? "dark" : "light"
  const colourModeRef = useRef(sectionColourMode)
  colourModeRef.current = sectionColourMode

  const { session, activeOrganization } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = session?.session?.token
  const orgId = activeOrganization?.organization.id

  // Keep a ref to the current preview hash so closures (setTimeout, etc.) always
  // read the latest value without causing re-renders or stale captures.
  const previewParamRef = useRef<string | null>(null)
  previewParamRef.current = searchParams.get("preview")

  const sectionCopy = useMemo(() => {
    return tenantCopy?.find((copy: any) => copy.type === sectionName)
  }, [tenantCopy, sectionName])

  // Helper to navigate while preserving the preview search param.
  // Uses react-router's { search } option to set params cleanly (no duplication).
  // Reads from ref to avoid stale closure issues in setTimeout callbacks.
  const navigateWithPreview = (path: string) => {
    const preview = previewParamRef.current
    if (preview) {
      const search = `?preview=${encodeURIComponent(preview)}`
      navigate({ pathname: path, search })
    } else {
      navigate(path)
    }
  }


  // Keep location ref in sync
  locationRef.current = location.pathname

  // Enhanced loading state management
  const {
    loadingState,
    updatePhase,
    setError,
    setTimeout: setTimeoutPhase,
    reset: resetLoadingState,
  } = useSectionLoadingState(sectionName)
  const [isSectionLoading, setIsSectionLoading] = useState(false)
  const [hasError, setHasError] = useState<Error | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Effect to update loading state based on section activity and content
  useEffect(() => {
    const wasActive = prevIsActiveRef.current
    const becameActive = !wasActive && isActive

    // When a section becomes active again, sync its MemoryRouter to the
    // current browser route instead of destroying and recreating it.
    // The section is already mounted in the shadow DOM — it just needs
    // its internal router updated.
    if (becameActive && rootCreatedRef.current && sectionLoadedRef.current) {
      const currentSubRoute = extractSectionSubRoute(location.pathname, location.search, sectionName)
      debug(sectionName, "ACTIVATED (already loaded) — syncing route to", currentSubRoute)
      if (browserNavigationCallbackRef.current) {
        browserNavigationCallbackRef.current(currentSubRoute)
      }
      lastSubRouteRef.current = currentSubRoute
    } else if (becameActive) {
      debug(sectionName, "ACTIVATED (not yet loaded)", {
        rootCreated: rootCreatedRef.current,
        sectionLoaded: sectionLoadedRef.current,
      })
    }

    // Update ref for next comparison
    prevIsActiveRef.current = isActive

    if (isActive) {
      // Check if section is already loaded before setting loading state
      const sectionKey = `${sectionName}-${orgId}-${token || "no-token"}`
      const isAlreadyLoaded = sectionLoadedRef.current === sectionKey && rootCreatedRef.current

      if (isAlreadyLoaded) {
        setIsSectionLoading(false)
        updatePhase("ready", `${sectionName} already loaded`)
        setHasError(null)
        onLoadStateChange?.(sectionName, false)
        return
      }

      setIsSectionLoading(true)
      setHasError(null)
      resetLoadingState()
      updatePhase("mounting")

      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      // Set a fallback timeout to stop loading after 10 seconds in case content never appears
      loadingTimeoutRef.current = setTimeout(() => {
        // Only timeout if section is still loading and not already created
        if (isSectionLoading && !rootCreatedRef.current) {
          setIsSectionLoading(false)
          setTimeoutPhase()
          setHasError(new Error(`Section ${sectionName} failed to load within 10 seconds`))
        }
        loadingTimeoutRef.current = null
      }, 10000)

      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current)
          loadingTimeoutRef.current = null
        }
      }
    } else {
      setIsSectionLoading(false)
      // Clear timeout when section becomes inactive
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
        loadingTimeoutRef.current = null
      }
    }
  }, [
    isActive,
    sectionName,
    orgId,
    token,
    isSectionLoading,
    rootCreatedRef.current,
    sectionLoadedRef.current,
    updatePhase,
    resetLoadingState,
    setTimeoutPhase,
    onLoadStateChange,
  ])

  // Helper to create a fresh section body element inside the shadow DOM.
  // Replacing the element (rather than clearing innerHTML) ensures React's
  // internal root tracking on the old element is discarded, so createRoot
  // on the new element works reliably.
  const createFreshBody = (shadowDom: ShadowRoot): HTMLElement => {
    const oldBody = shadowDom.querySelector("#section-body")
    debug(sectionName, "createFreshBody", { hadOldBody: !!oldBody })
    if (oldBody) {
      oldBody.remove()
    }

    const body = document.createElement("div")
    body.style.height = "calc(100vh - 5px)"
    body.style.overflow = "hidden"
    body.style.width = "100%"
    body.style.minWidth = "300px"
    body.style.fontFamily = "montserrat !important"
    body.id = "section-body"
    body.classList.add(colourModeRef.current)
    shadowDom.appendChild(body)
    bodyRef.current = body
    rootCreatedRef.current = false

    // Log computed dimensions after append (will be 0 if host isn't in the document yet)
    requestAnimationFrame(() => {
      const rect = body.getBoundingClientRect()
      debug(sectionName, "body dimensions after append", {
        width: rect.width,
        height: rect.height,
        inDocument: body.isConnected,
      })
    })

    return body
  }

  // Follow Colour Mode changes live on an already-mounted section body.
  useEffect(() => {
    const body = bodyRef.current
    if (!body) return
    body.classList.remove("light", "dark")
    body.classList.add(sectionColourMode)
  }, [sectionColourMode])

  // Retry handler for failed section loads
  const handleRetry = () => {
    setRetryCount((prev) => prev + 1)
    setHasError(null)
    setIsSectionLoading(true)
    resetLoadingState()

    // Clear existing section state to force reload
    sectionLoadedRef.current = null
    rootCreatedRef.current = false
    bodyRef.current = null

    // Replace the body element entirely so createRoot gets a fresh container
    if (shadowRef.current) {
      createFreshBody(shadowRef.current)
    }
  }

  useEffect(() => {
    // Check if we should reload due to reloadTrigger change
    const shouldReload = reloadTrigger > 0 && rootCreatedRef.current

    // Only proceed with shadow DOM creation if we should be loading the section
    // The container is only rendered when !rootCreatedRef.current && !hasError, OR when we need to reload
    if (!containerRef.current || (rootCreatedRef.current && !shouldReload) || hasError) {
      debug(sectionName, "main effect — SKIPPED", {
        hasContainer: !!containerRef.current,
        rootCreated: rootCreatedRef.current,
        shouldReload,
        hasError: !!hasError,
      })
      return
    }

    debug(sectionName, "main effect — RUNNING", {
      shouldReload,
      reloadTrigger,
      orgId,
      isActive,
      sectionLoaded: sectionLoadedRef.current,
    })

    // If we're reloading, replace the existing section body with a fresh element
    if (shouldReload) {
      debug(sectionName, "RELOADING — replacing body element")
      if (shadowRef.current) {
        createFreshBody(shadowRef.current)
      }
      sectionLoadedRef.current = null
      rootCreatedRef.current = false
    }

    // Check if current route is a global settings route - if so, don't load the section
    if (isGlobalSettingsRoute(location.pathname)) {
      onLoadStateChange?.(sectionName, false)
      return
    }

    // Always render into shadow DOM (for style isolation and controlled CSS injection)
    let shadowDom = shadowRef.current
    if (!shadowDom) {
      shadowDom = containerRef.current.shadowRoot ?? containerRef.current.attachShadow({ mode: "open" })
      shadowRef.current = shadowDom
    }

    // Create a unique key for this section instance (section + org + token)
    // Token is included because sections might need to reload if auth changes
    // Search is NOT included because query params should be handled internally by MemoryRouter
    const sectionKey = `${sectionName}-${orgId}-${token || "no-token"}`

    // Check if section is already loaded by looking for the body element in shadow DOM
    const existingBody = shadowDom.querySelector("#section-body") as HTMLElement

    // If this is the same section instance and the body exists, don't reload
    if (sectionLoadedRef.current === sectionKey && existingBody && rootCreatedRef.current) {
      debug(sectionName, "already loaded — skipping", { sectionKey })
      bodyRef.current = existingBody
      // Clear loading state since section is already loaded
      setIsSectionLoading(false)
      // Update to ready state since section is already loaded
      updatePhase("ready", `${sectionName} already loaded`)
      // Clear any errors
      setHasError(null)
      // Notify that loading is complete (already loaded)
      onLoadStateChange?.(sectionName, false)
      return
    }

    // Create or reuse body element inside shadow DOM
    let body = bodyRef.current || (shadowDom.querySelector("#section-body") as HTMLElement)

    // If section key changed (reloading), replace the body to give createRoot a fresh container
    const isReloading = sectionLoadedRef.current !== null && sectionLoadedRef.current !== sectionKey

    if (!body) {
      debug(sectionName, "creating INITIAL body element")
      body = createFreshBody(shadowDom)
    } else if (isReloading) {
      debug(sectionName, "REPLACING body for reload", {
        oldKey: sectionLoadedRef.current,
        newKey: sectionKey,
      })
      body = createFreshBody(shadowDom)
    } else {
      debug(sectionName, "REUSING existing body element")
    }

    // Inject global/shared styles into shadow DOM once
    if (!shadowDom.querySelector("style[data-shadow-global]")) {
      const portalStyle = document.createElement("style")
      portalStyle.setAttribute("data-shadow-global", "true")
      portalStyle.textContent = shadowGlobalCss
      shadowDom.appendChild(portalStyle)
    }

    // Track if this is the initial load (not a reload) - check before async load
    const isInitialLoad = sectionLoadedRef.current === null || sectionLoadedRef.current !== sectionKey

    // Load the section createSection function and call it with MemoryRouter
    const loadSection = async () => {
      try {
        // Notify that we're starting to load
        onLoadStateChange?.(sectionName, true)
        updatePhase("loading-module", `Loading ${sectionName} module...`)

        // Extract the current sub-route from browser URL for initial memory router state
        // URL is always the source of truth on page load/refresh (preserving section query params while filtering hub-level params)
        const initialSubRoute = extractSectionSubRoute(location.pathname, location.search, sectionName)

        // Create debounced route change handler to sync section navigation back to browser
        // This prevents rapid successive navigation calls that can cause delays
        const handleRouteChange = createRouteChangeHandler(sectionName, (fullPath) => {
          // CRITICAL FIX: Don't process route changes if this section is not active
          if (!isActive) {
            return
          }

          // Determine if this is a navigation during initial section load vs runtime navigation
          // Runtime navigation should always be allowed, initial load navigation should be selective
          // CRITICAL: Only consider it runtime navigation if the section was fully loaded AND this is not the initial load
          const isRuntimeNavigation =
            rootCreatedRef.current && sectionLoadedRef.current === sectionKey && !isInitialLoad

          // Handle navigation to section home route carefully
          const sectionHome = `/${sectionName}`
          const isNavigatingToHome = fullPath === sectionHome
          const isBrowserOnSubRoute = locationRef.current.startsWith(sectionHome + "/")

          if (isNavigatingToHome && isBrowserOnSubRoute && !isInitialLoad) {
            // Block navigation to home route during normal operation if browser is on sub-route
            // This prevents sections from overriding the URL when they can't find specific item data
            console.warn(
              `[SectionLoader:${sectionName}] BLOCKING navigation to home route - Browser is on sub-route, section trying to navigate to home during normal operation`
            )
            return
          }

          if (isNavigatingToHome && isBrowserOnSubRoute && isInitialLoad) {
            // Allow navigation to home route during initial load even if browser is on sub-route
            // This handles cases where the section legitimately initialises to home
          }

          // FIXED: Only skip navigation during initial load if route matches AND it's not runtime navigation
          // Runtime navigation (user clicking buttons) should always proceed even if URLs match
          // CRITICAL: When isRuntimeNavigation is true, we need to force MemoryRouter sync regardless of URL match
          if (isInitialLoad && !isRuntimeNavigation && fullPath === locationRef.current) {
            return
          }

          // CRITICAL FIX: If this is runtime navigation from browser URL change, we need to force
          // the section's MemoryRouter to sync, even if browser URLs match
          if (isRuntimeNavigation && fullPath === locationRef.current) {
            // Don't return here - proceed with navigation to sync MemoryRouter
          }
          // Navigate if the target path is different from current browser URL
          // OR if this is runtime navigation that needs to sync MemoryRouter
          const currentPath = locationRef.current
          const needsNavigation = fullPath !== currentPath || isRuntimeNavigation

          if (needsNavigation) {
            // Store the pending navigation
            pendingNavigationRef.current = fullPath

            // Clear any existing timeout
            if (navigationTimeoutRef.current) {
              clearTimeout(navigationTimeoutRef.current)
            }

            // Debounce navigation by 10ms to batch rapid route changes while being more responsive
            navigationTimeoutRef.current = setTimeout(() => {
              const targetPath = pendingNavigationRef.current
              if (targetPath) {
                // Only update browser URL if it's actually different
                if (targetPath !== locationRef.current) {
                  locationRef.current = targetPath

                  // Update stored sub-route in Redux before navigating
                  const subRoute = extractSubRoute(targetPath, sectionName)
                  updateSectionSubRoute(sectionName, subRoute)

                  // Ensure URL is always the source of truth - navigate to the target path
                  navigateWithPreview(targetPath)
                } else {
                  // URLs match but we still need to sync MemoryRouter for runtime navigation
                }
              }
              pendingNavigationRef.current = null
              navigationTimeoutRef.current = null
            }, 10)
          } else {
            // No navigation needed
            // Clear any pending navigation since we're already at the target
            if (navigationTimeoutRef.current) {
              clearTimeout(navigationTimeoutRef.current)
              navigationTimeoutRef.current = null
            }
            pendingNavigationRef.current = null
          }
        })

        // Store the route change handler for later use (expects sub-route, not full path)
        routeChangeHandlerRef.current = (subRoute) => {
          // Call the section's internal route change handler directly with the sub-route
          handleRouteChange(subRoute)
        }

        // Preview mode: when a preview bundle URL is provided, skip promotion resolution
        // and load directly from the PR preview bundle.
        // The bundle exports a map of section name → createSection function, each using
        // the same 12-arg native calling convention from createSectionFactory.
        if (previewBundleUrl) {
          updatePhase("loading-module", `Loading PR preview for ${sectionName}...`)
          try {
            const bundleModule = await loadPrototypeBundle(previewBundleUrl)

            // The bundle's default export is a map: { customers: fn, assets: fn, ... }
            // Look up the createSection function for this specific section
            const sectionMap = bundleModule.default as unknown as Record<string, (...args: unknown[]) => void>
            const previewCreateSection = sectionMap[sectionName]

            if (!previewCreateSection || typeof previewCreateSection !== "function") {
              throw new Error(
                `Preview bundle does not contain section '${sectionName}'. Available: ${Object.keys(sectionMap).join(", ")}`
              )
            }

            updatePhase("initializing", `Setting up PR preview ${sectionName} section...`)

            if (!rootCreatedRef.current) {
              updatePhase("rendering", `Rendering PR preview ${sectionName} interface...`)

              // Call using the native 12-arg calling convention (same as the native path below)
              // Pass undefined for Router so the section's factory uses its own bundled
              // MemoryRouter (auto-selected when initialEntries are provided), avoiding
              // duplicate-React issues from mixing the hub's React Router with the bundle's React.
              previewCreateSection(
                body,
                shadowDom,
                false, // dashboard
                user,
                sectionCopy,
                undefined, // Router — let the section use its own MemoryRouter
                token,
                "better-auth",
                tenantFeatures,
                {
                  primaryGraphqlUri: graphqlUri,
                  legacyGraphqlUri: graphqlUriLegacy,
                  restEndpoint: restEndpoint,
                  initialEntries: [initialSubRoute],
                  onRouteChange: handleRouteChange,
                  navigate: navigate,
                  onBrowserNavigationChange: (callback: (subRoute: string) => void) => {
                    browserNavigationCallbackRef.current = callback
                  },
                } as any,
                sectionFeatures || {},
                profile
              )

              rootCreatedRef.current = true
              sectionLoadedRef.current = sectionKey
              lastSubRouteRef.current = initialSubRoute
              updatePhase("ready", `${sectionName} (PR preview) loaded successfully`)
              setHasError(null)
              onLoadStateChange?.(sectionName, false)
            } else {
              sectionLoadedRef.current = sectionKey
              updatePhase("ready", `${sectionName} (PR preview) already loaded`)
              setHasError(null)
              onLoadStateChange?.(sectionName, false)
            }

            return // Skip the rest of loadSection (resolveSection flow)
          } catch (previewError) {
            console.warn(
              `[SectionLoader:${sectionName}] Preview bundle failed to load, falling back to default`,
              previewError
            )
            // Fall through to normal resolveSection flow
          }
        }

        // Resolve whether this section should load natively or from a promoted prototype
        const resolution = resolveSection(sectionName, promotionOverrides)

        if (resolution.type === "promoted") {
          // --- Promoted path: load prototype bundle from S3 ---
          updatePhase("loading-module", `Loading promoted prototype for ${sectionName}...`)

          const prototypeModule = await loadPrototypeBundle(resolution.bundleUrl)

          updatePhase("initializing", `Setting up promoted ${sectionName} section...`)

          if (!rootCreatedRef.current) {
            updatePhase("rendering", `Rendering promoted ${sectionName} interface...`)

            // Build the section context for the promoted prototype
            const sectionContext = buildSectionContext(sectionName, {
              user,
              token,
              sectionFeatures: sectionFeatures || {},
              tenantCopy: sectionCopy,
              tenantFeatures,
              profile: (profile as string) ?? "",
              initialSubRoute,
              onRouteChange: handleRouteChange,
              onBrowserNavigationChange: (callback: (subRoute: string) => void) => {
                browserNavigationCallbackRef.current = callback
              },
              navigate,
            })

            // Mount the promoted prototype with the 3-arg signature
            const mount = prototypeModule.default as (container: HTMLElement, shadowRoot: ShadowRoot, context: unknown) => void
            mount(body, shadowDom, sectionContext)

            // Mark that we've created a root for this body
            rootCreatedRef.current = true
            // Mark this section as loaded
            sectionLoadedRef.current = sectionKey
            // Store the initial sub-route
            lastSubRouteRef.current = initialSubRoute
            // Update to ready state
            updatePhase("ready", `${sectionName} (promoted) loaded successfully`)
            // Clear any errors
            setHasError(null)
            // Notify that loading is complete
            onLoadStateChange?.(sectionName, false)
          } else {
            // Still mark as loaded even if we skipped (in case of race conditions)
            sectionLoadedRef.current = sectionKey
            updatePhase("ready", `${sectionName} (promoted) already loaded`)
            setHasError(null)
            onLoadStateChange?.(sectionName, false)
          }
        } else {
          // --- Native path: existing behavior unchanged ---

          // Check if the section exists in the sections object
          if (!sections[sectionName]) {
            throw new Error(
              `Section '${sectionName}' not found in sections object. Available sections: ${Object.keys(sections).join(
                ", "
              )}`
            )
          }

          // Get the section importer function
          const sectionImporter = sections[sectionName]

          const module = await sectionImporter()

          updatePhase("initializing", `Setting up ${sectionName} section...`)

          if (module && module.default && typeof module.default === "function") {
            // Only call createSection if we haven't already created a root for this body
            // This prevents the "container has already been passed to createRoot()" error
            if (!rootCreatedRef.current) {
              updatePhase("rendering", `Rendering ${sectionName} interface...`)

              debug(sectionName, "calling createSection (native)", {
                bodyConnected: body.isConnected,
                bodyRect: body.getBoundingClientRect(),
                initialSubRoute,
              })

              // Call the createSection function with MemoryRouter and route sync
              // (container, shadow, dashboard, user, copy, Router, token, authType, features, endpointOverrides, sectionFeatures)
              module.default(
                body,
                shadowDom,
                false, // dashboard
                user, // Pass the user from hub's auth context
                sectionCopy, // tenant copy from preferences
                MemoryRouter, // Router component - use memory router for isolation
                token, // better-auth token
                "better-auth", // authType
                tenantFeatures, // tenant features from preferences
                {
                  // Provide hub's dual endpoints to sections
                  primaryGraphqlUri: graphqlUri, // Hub's modern GraphQL
                  legacyGraphqlUri: graphqlUriLegacy, // Section's legacy GraphQL
                  restEndpoint: restEndpoint,
                  // Add section routing context
                  initialEntries: [initialSubRoute], // Set initial route for memory router with query params
                  onRouteChange: handleRouteChange, // Callback for section navigation
                  navigate: navigate, // Hub's navigate function for cross-section navigation
                  // Browser-to-section navigation sync: sections register their navigation handler here
                  onBrowserNavigationChange: (callback: (subRoute: string) => void) => {
                    browserNavigationCallbackRef.current = callback
                  },
                } as any,
                sectionFeatures || {}, // Section-level features from tenant_feature table
                profile // Tenant profile from preferences
              )

              // Mark that we've created a root for this body
              rootCreatedRef.current = true
              // Mark this section as loaded
              sectionLoadedRef.current = sectionKey
              // Store the initial sub-route
              lastSubRouteRef.current = initialSubRoute
              // Update to ready state
              updatePhase("ready", `${sectionName} loaded successfully`)
              debug(sectionName, "MOUNTED successfully", { sectionKey })
              // Clear any errors
              setHasError(null)
              // Notify that loading is complete
              onLoadStateChange?.(sectionName, false)
            } else {
              // Still mark as loaded even if we skipped (in case of race conditions)
              sectionLoadedRef.current = sectionKey
              // Update to ready state since section is already loaded
              updatePhase("ready", `${sectionName} already loaded`)
              // Clear any errors
              setHasError(null)
              // Notify that loading is complete (already loaded)
              onLoadStateChange?.(sectionName, false)
            }
          } else {
            const errorMsg = `Section module does not export a default function: ${sectionName}`
            console.error(errorMsg)
            const error = new Error(errorMsg)
            setError(error)
            setHasError(error)
            onLoadStateChange?.(sectionName, false)
          }
        }
      } catch (error) {
        const sectionError = error instanceof Error ? error : new Error(`Failed to load section: ${sectionName}`)
        setError(sectionError)
        setHasError(sectionError)
        onLoadStateChange?.(sectionName, false)
      }
    }

    // Wrap loadSection call in try-catch to handle any synchronous errors
    try {
      loadSection()
    } catch (syncError) {
      console.error(`[SectionLoader] Synchronous error in loadSection for ${sectionName}:`, syncError)
      const error = syncError instanceof Error ? syncError : new Error(`Synchronous error loading ${sectionName}`)
      setError(error)
      setHasError(error)
      onLoadStateChange?.(sectionName, false)
    }

    return () => {
      // Clear any pending navigation timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current)
        navigationTimeoutRef.current = null
      }
      pendingNavigationRef.current = null

      // Only tear down the section when deps actually change (org switch, token refresh, etc).
      // React StrictMode double-invokes effects with the SAME deps — in that case we leave
      // everything intact so the section isn't destroyed and recreated unnecessarily.
      // The unmount cleanup effect (the [] one) handles final teardown.
    }
    // Note: sectionFeatures is intentionally NOT in dependencies because:
    // 1. Features don't change frequently
    // 2. Sections should handle feature changes internally without reloading
    // 3. Including it causes unnecessary reloads when the object reference changes
    // reloadTrigger is included to force section re-initialization when needed (e.g., navigate to home)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionName, orgId, token, reloadTrigger])

  // Log lifecycle state changes
  useEffect(() => {
    debug(sectionName, "state", {
      isActive,
      hasError: !!hasError,
      isSectionLoading,
      phase: loadingState.phase,
      rootCreated: rootCreatedRef.current,
      sectionLoaded: sectionLoadedRef.current,
    })
  }, [sectionName, isActive, hasError, isSectionLoading, loadingState.phase])

  // Handle route changes for already-loaded sections
  useEffect(() => {
    // Only handle route changes if section is already loaded and active
    if (rootCreatedRef.current && sectionLoadedRef.current && isActive) {
      // Check if the current URL actually belongs to this section
      // If not, don't handle internal route changes (let hub navigation take over)
      const routeInfo = parseRouteFromPath(location.pathname)
      if (routeInfo?.sectionName !== sectionName) {
        return
      }

      const currentSubRoute = extractSectionSubRoute(location.pathname, location.search, sectionName)

      // Check if the route actually changed
      if (currentSubRoute !== lastSubRouteRef.current) {
        // Route changed - proceed with normal internal navigation

        // Force reload logic removed - now handled by reloadTrigger in main useEffect

        // Sync browser navigation to section's MemoryRouter
        // This handles browser back/forward button navigation
        if (browserNavigationCallbackRef.current) {
          browserNavigationCallbackRef.current(currentSubRoute)
        }

        // Trigger internal navigation within the section (for section-to-browser sync)
        if (routeChangeHandlerRef.current) {
          routeChangeHandlerRef.current(currentSubRoute)
        }

        lastSubRouteRef.current = currentSubRoute
      }
    }
  }, [location.pathname, location.search, sectionName, isActive])

  // No separate unmount cleanup needed. When the component unmounts due to a key
  // change (org switch), the old containerRef DOM element is removed from the document,
  // taking its shadow root and body with it. For reloads within the same component
  // instance, createFreshBody handles replacing the body element.

  // Show error state if there's an error
  if (hasError) {
    // Show a friendly "new version available" prompt for stale chunk errors
    if (hasError instanceof ChunkStaleError) {
      return (
        <Box
          px={"16px"}
          width="100%"
          minWidth="320px"
          height="100%"
          fontFamily="montserrat !important"
        >
          <Center minH="400px" flexDirection="column" gap={4}>
            <Box
              bg="#39aed2"
              color="white"
              borderRadius="lg"
              p={6}
              maxW="420px"
              textAlign="center"
              shadow="lg"
            >
              <Text fontSize="xl" fontWeight="bold" mb={2}>
                New features available
              </Text>
              <Text fontSize="sm" mb={4}>
                A new version of the platform has been deployed. Please refresh to load the latest features.
              </Text>
              <Button
                onClick={() => refreshApp()}
                bg="card"
                color="#39aed2"
                _hover={{ bg: "bg.muted" }}
                fontWeight="semibold"
              >
                Refresh now
              </Button>
            </Box>
          </Center>
        </Box>
      )
    }

    return (
      <Box
        px={"16px"}
        width="100%"
        minWidth="320px"
        height="100%"
        fontFamily="montserrat !important"
      >
        <SectionErrorBoundary
          sectionName={sectionName}
          onRetry={handleRetry}
          fallback={
            <Center minH="400px" flexDirection="column" gap={4}>
              <Text fontSize="xl" fontWeight="bold" color="red.500">
                Failed to load {sectionName} section
              </Text>
              <Text color="fg.muted">{hasError.message}</Text>
              <Button onClick={handleRetry} colorPalette="blue">
                Retry {retryCount > 0 ? `(${retryCount})` : ""}
              </Button>
            </Center>
          }
        >
          <div />
        </SectionErrorBoundary>
      </Box>
    )
  }

  return (
    <SectionErrorBoundary sectionName={sectionName} onRetry={handleRetry}>
      <Box
        ref={containerRef}
        px={"8px"}
        borderRadius={"5px"}
        border={"none"}
        boxShadow={"0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)"}
        width="100%"
        minWidth="320px"
        height="100%"
        fontFamily="montserrat !important"
        position="relative"
        bg={searchParams.get("preview") === "papa-15" ? "#F2F0EF" : "background"}
        id="section"
      >
        {/* Shadow DOM container - the actual section content will be rendered here via useEffect */}
        {/* This div serves as the container for the shadow DOM */}
      </Box>
    </SectionErrorBoundary>
  )
}
